import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  bootstrapGenre,
  bootstrapPlaylist,
  deleteResolvedArtist,
  fetchResolvedArtists,
  importResolvedArtist,
  ingestResolvedArtist,
  previewPlaylist,
  type ResolvedArtist,
} from "@/api/artist_resolver";
import { useToast } from "@/components/ToastProvider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { SSEEvent, useEventStream } from "@/hooks/useEventStream";

const DEFAULT_GENRE = "jamgrass";
const PLAYLIST_PREVIEW_PAGE_SIZE = 500;
const RESOLVED_PAGE_SIZE_OPTIONS = [25, 50, 100];

type ArtistIngestStatus = {
  taskId?: string;
  status: "queued" | "in_progress" | "complete" | "complete_with_errors" | "failed";
  insertedEvents?: number | null;
  venuesTouched?: number | null;
  errors?: string[];
  updatedAt: number;
};

const formatTargetLabel = (raw?: string | null) => {
  if (!raw) return "task";
  if (raw.startsWith("playlist:")) {
    const parts = raw.split(":");
    return `playlist ${parts.slice(1).join(":") || parts[0]}`;
  }
  return raw;
};

const extractPlaylistId = (value?: string | null) => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("spotify:")) {
    const parts = trimmed.split(":");
    return parts[parts.length - 1] || null;
  }
  if (trimmed.includes("open.spotify.com")) {
    const withoutQuery = trimmed.split("?")[0];
    const segments = withoutQuery.replace(/\/+$/, "").split("/");
    return segments[segments.length - 1] || null;
  }
  return trimmed;
};

export default function ArtistsPage() {
  const [genreInput, setGenreInput] = useState(DEFAULT_GENRE);
  const [playlistInput, setPlaylistInput] = useState("");
  const [taskId, setTaskId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [bootstrapStatus, setBootstrapStatus] = useState("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPlaylistSubmitting, setIsPlaylistSubmitting] = useState(false);
  const [resolvedArtists, setResolvedArtists] = useState<ResolvedArtist[]>([]);
  const [resolvedLoading, setResolvedLoading] = useState(false);
  const [resolvedError, setResolvedError] = useState<string | null>(null);
  const [resolvedLimit, setResolvedLimit] = useState(50);
  const [resolvedOffset, setResolvedOffset] = useState(0);
  const [resolvedTotal, setResolvedTotal] = useState(0);
  const [resolvedPlaylistFilter, setResolvedPlaylistFilter] = useState<string | null>(null);
  const [importingArtistId, setImportingArtistId] = useState<number | null>(null);
  const [ingestStatuses, setIngestStatuses] = useState<Record<number, ArtistIngestStatus>>({});
  const [deletingArtistId, setDeletingArtistId] = useState<number | null>(null);
  const [selectedArtistIds, setSelectedArtistIds] = useState<Set<number>>(new Set());
  const [bulkAction, setBulkAction] = useState<"ingest" | "import" | "delete" | null>(null);
  const [playlistPreview, setPlaylistPreview] = useState<
    { name: string; spotify_id: string; followers: number; genres: string[] }[]
  >([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewOffset, setPreviewOffset] = useState(0);
  const [previewHasMore, setPreviewHasMore] = useState(false);
  const { push } = useToast();
  const taskIdRef = useRef<string | null>(null);

  useEffect(() => {
    taskIdRef.current = taskId;
  }, [taskId]);

  const handleEvent = useCallback(
    (event: SSEEvent) => {
      if (event.event === "artist_bootstrap_progress") {
        const payload = event.data as {
          task_id: string;
          genre: string;
          status: string;
          progress: number;
          count?: number;
        };
        if (!taskIdRef.current || payload.task_id !== taskIdRef.current) {
          return;
        }

        const targetLabel = formatTargetLabel(payload.genre);

        setProgress(payload.progress ?? 0);
        setBootstrapStatus(payload.status ?? "in_progress");
        if (payload.status === "complete") {
          const count = payload.count ?? 0;
          const noun = count === 1 ? "artist" : "artists";
          setMessage(
            count
              ? `Imported ${count} new ${noun} from ${targetLabel}.`
              : `No new artists to import from ${targetLabel}.`,
          );
        } else {
          setMessage(`${targetLabel}: ${payload.status.replace("_", " ")}`);
        }
        return;
      }

      if (event.event === "artist_ingest_progress") {
        const payload = event.data as {
          artist_id?: number;
          task_id?: string;
          status?: string;
          inserted_events?: number | null;
          venues_touched?: number | null;
          errors?: string[];
        };
        if (!payload.artist_id || !payload.status) {
          return;
        }
        const normalizedStatus =
          payload.status === "starting"
            ? "in_progress"
            : payload.status === "queued"
              ? "queued"
              : payload.status === "complete_with_errors"
                ? "complete_with_errors"
                : payload.status === "complete"
                  ? "complete"
                  : payload.status === "failed"
                    ? "failed"
                    : "in_progress";

        setIngestStatuses((prev) => {
          const existing = prev[payload.artist_id];
          if (
            existing &&
            existing.taskId &&
            payload.task_id &&
            existing.taskId !== payload.task_id &&
            normalizedStatus !== "queued"
          ) {
            return prev;
          }
          const resetMetrics = normalizedStatus === "queued" || normalizedStatus === "in_progress";
          const next: ArtistIngestStatus = {
            taskId: payload.task_id ?? existing?.taskId,
            status: normalizedStatus,
            insertedEvents: resetMetrics ? null : payload.inserted_events ?? existing?.insertedEvents ?? null,
            venuesTouched: resetMetrics ? null : payload.venues_touched ?? existing?.venuesTouched ?? null,
            errors: resetMetrics ? [] : payload.errors ?? existing?.errors ?? [],
            updatedAt: Date.now(),
          };
          return {
            ...prev,
            [payload.artist_id!]: next,
          };
        });

        if (normalizedStatus === "complete") {
          const venues = payload.venues_touched ?? 0;
          const inserted = payload.inserted_events ?? 0;
          push({
            title: "Ingestion complete",
            message: `Processed ${venues} venues and ${inserted} events.`,
          });
        } else if (normalizedStatus === "complete_with_errors") {
          const venues = payload.venues_touched ?? 0;
          const inserted = payload.inserted_events ?? 0;
          push({
            title: "Ingestion completed with issues",
            message: `Processed ${venues} venues / ${inserted} events.`,
          });
        } else if (normalizedStatus === "failed") {
          const errorMessage = (payload.errors && payload.errors[0]) || "Unable to ingest venues.";
          push({
            title: "Ingestion failed",
            message: errorMessage,
          });
        }
      }
    },
    [push],
  );

  const { connected } = useEventStream(handleEvent);

  const loadResolvedArtists = useCallback(async () => {
    setResolvedLoading(true);
    setResolvedError(null);
    try {
      const payload = await fetchResolvedArtists({
        limit: resolvedLimit,
        offset: resolvedOffset,
        playlist: resolvedPlaylistFilter ?? undefined,
      });
      const artists = payload.artists ?? [];
      setResolvedArtists(artists);
      setResolvedTotal(payload.total ?? 0);
      setSelectedArtistIds((prev) => {
        if (prev.size === 0) return prev;
        const allowed = new Set(artists.map((artist) => artist.id));
        return new Set(
          Array.from(prev).filter((id) => allowed.has(id)),
        );
      });
    } catch (error) {
      setResolvedError(error instanceof Error ? error.message : "Unable to load resolved artists.");
    } finally {
      setResolvedLoading(false);
    }
  }, [resolvedLimit, resolvedOffset, resolvedPlaylistFilter]);

  useEffect(() => {
    void loadResolvedArtists();
  }, [loadResolvedArtists]);

  useEffect(() => {
    if (bootstrapStatus === "complete") {
      void loadResolvedArtists();
    }
  }, [bootstrapStatus, loadResolvedArtists]);

  const visibleArtistIds = useMemo(() => resolvedArtists.map((artist) => artist.id), [resolvedArtists]);
  const selectedArtists = useMemo(
    () => resolvedArtists.filter((artist) => selectedArtistIds.has(artist.id)),
    [resolvedArtists, selectedArtistIds],
  );
  const allSelected =
    visibleArtistIds.length > 0 && visibleArtistIds.every((artistId) => selectedArtistIds.has(artistId));
  const selectedCount = selectedArtistIds.size;
  const totalPages =
    resolvedTotal > 0 ? Math.ceil(resolvedTotal / resolvedLimit) : resolvedArtists.length > 0 ? 1 : 0;
  const currentPage = totalPages ? Math.floor(resolvedOffset / resolvedLimit) + 1 : 0;
  const pageOptions = totalPages
    ? Array.from({ length: totalPages }, (_, index) => index + 1)
    : [1];
  const effectiveCurrentPage = currentPage || 1;
  const canGoPrevious = currentPage > 1;
  const canGoNext = totalPages ? currentPage < totalPages : false;

  const toggleSelectAll = useCallback(() => {
    setSelectedArtistIds((prev) => {
      if (allSelected) {
        return new Set();
      }
      return new Set(visibleArtistIds);
    });
  }, [allSelected, visibleArtistIds]);

  const handleResolvedLimitChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = Number(event.target.value);
    setResolvedLimit(value);
    setResolvedOffset(0);
  };

  const handleResolvedPageSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    const page = Number(event.target.value);
    if (!Number.isFinite(page) || page < 1) {
      return;
    }
    const nextOffset = (page - 1) * resolvedLimit;
    if (nextOffset !== resolvedOffset) {
      setResolvedOffset(nextOffset);
    }
  };

  const handleResolvedPageChange = (direction: "previous" | "next") => {
    if (direction === "previous" && canGoPrevious) {
      setResolvedOffset(Math.max(resolvedOffset - resolvedLimit, 0));
    }
    if (direction === "next" && canGoNext) {
      setResolvedOffset(resolvedOffset + resolvedLimit);
    }
  };

  const renderResolvedPagination = (position: "top" | "bottom") => (
    <div
      key={`resolved-pagination-${position}`}
      className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground"
    >
      <div className="flex items-center gap-2">
        <span>Rows per page</span>
        <select
          className="rounded-lg border border-border bg-background px-2 py-1 text-foreground/80"
          value={resolvedLimit}
          onChange={handleResolvedLimitChange}
        >
          {RESOLVED_PAGE_SIZE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-full text-xs"
          onClick={() => handleResolvedPageChange("previous")}
          disabled={!canGoPrevious || resolvedLoading}
        >
          Previous
        </Button>
        <select
          className="rounded-lg border border-border bg-background px-2 py-1 text-foreground/80"
          value={effectiveCurrentPage}
          onChange={handleResolvedPageSelect}
          disabled={totalPages === 0 || resolvedLoading}
        >
          {pageOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <span>of {totalPages || 0}</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-full text-xs"
          onClick={() => handleResolvedPageChange("next")}
          disabled={!canGoNext || resolvedLoading}
        >
          Next
        </Button>
      </div>
    </div>
  );

  const toggleArtistSelection = useCallback((artistId: number) => {
    setSelectedArtistIds((prev) => {
      const next = new Set(prev);
      if (next.has(artistId)) {
        next.delete(artistId);
      } else {
        next.add(artistId);
      }
      return next;
    });
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = genreInput.trim();
    if (!trimmed) {
      setMessage("Genre is required.");
      return;
    }

    setIsSubmitting(true);
    setBootstrapStatus("queued");
    setProgress(0);
    setMessage("Queueing find task...");

    try {
      const payload = await bootstrapGenre(trimmed);
      setTaskId(payload.task_id);
      setMessage("Find task enqueued. Streaming progress…");
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Failed to start find."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePlaylistSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedPlaylist = playlistInput.trim();
    if (!trimmedPlaylist) {
      setMessage("Playlist URL or ID is required.");
      return;
    }
    const playlistId = extractPlaylistId(trimmedPlaylist);
    if (playlistId) {
      setResolvedPlaylistFilter(playlistId);
      setResolvedOffset(0);
    }

    setIsPlaylistSubmitting(true);
    setBootstrapStatus("queued");
    setProgress(0);
    setMessage("Queueing playlist find task...");

    try {
      const payload = await bootstrapPlaylist({
        playlist: trimmedPlaylist,
      });
      setTaskId(payload.task_id);
      setMessage("Playlist find enqueued. Streaming progress…");
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Failed to start playlist find."
      );
    } finally {
      setIsPlaylistSubmitting(false);
    }
  };

  const runPlaylistPreview = useCallback(
    async (nextOffset = 0) => {
      const trimmedPlaylist = playlistInput.trim();
      if (!trimmedPlaylist) {
        setMessage("Playlist URL or ID is required for preview.");
        return;
      }
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const response = await previewPlaylist({
          playlist: trimmedPlaylist,
          limit: PLAYLIST_PREVIEW_PAGE_SIZE,
          offset: nextOffset,
        });
        setPlaylistPreview(response.artists);
        setPreviewOffset(response.offset);
        setPreviewHasMore(response.has_more);
      } catch (error) {
        setPreviewError(error instanceof Error ? error.message : "Failed to preview playlist.");
      } finally {
        setPreviewLoading(false);
      }
    },
    [playlistInput],
  );

  const handlePlaylistPreview = () => {
    void runPlaylistPreview(0);
  };

  const handlePreviewPageChange = (direction: "previous" | "next") => {
    if (direction === "previous") {
      const nextOffset = Math.max(previewOffset - PLAYLIST_PREVIEW_PAGE_SIZE, 0);
      if (nextOffset !== previewOffset) {
        void runPlaylistPreview(nextOffset);
      }
    } else if (previewHasMore) {
      void runPlaylistPreview(previewOffset + PLAYLIST_PREVIEW_PAGE_SIZE);
    }
  };

  const handleImportArtist = useCallback(
    async (artistId: number) => {
      setImportingArtistId(artistId);
      try {
        const response = await importResolvedArtist(artistId);
        push({
          title: "CRM updated",
          message: response.status === "created" ? "Artist contact added." : "Artist contact refreshed.",
        });
        await loadResolvedArtists();
      } catch (error) {
        push({
          title: "Import failed",
          message: error instanceof Error ? error.message : "Unable to add artist to CRM.",
        });
      } finally {
        setImportingArtistId(null);
      }
    },
    [loadResolvedArtists, push],
  );

  const handleIngestArtist = useCallback(
    async (artistId: number) => {
      setIngestStatuses((prev) => ({
        ...prev,
        [artistId]: {
          taskId: prev[artistId]?.taskId,
          status: "in_progress",
          insertedEvents: null,
          venuesTouched: null,
          errors: [],
          updatedAt: Date.now(),
        },
      }));
      try {
        const response = await ingestResolvedArtist(artistId);
        setIngestStatuses((prev) => ({
          ...prev,
          [artistId]: {
            taskId: prev[artistId]?.taskId,
            status: response.errors && response.errors.length ? "complete_with_errors" : "complete",
            insertedEvents: response.inserted_events ?? null,
            venuesTouched: response.venues_touched ?? null,
            errors: response.errors ?? [],
            updatedAt: Date.now(),
          },
        }));
        const created = response.crm_contacts_created ?? 0;
        const updated = response.crm_contacts_updated ?? 0;
        const failed = response.crm_contacts_failed ?? 0;
        const synced = created + updated;
        if (synced || failed) {
          const messageParts: string[] = [];
          if (synced) {
            messageParts.push(`Synced ${synced} venue${synced === 1 ? "" : "s"} to CRM.`);
          }
          if (failed) {
            messageParts.push(`${failed} failed to sync.`);
          }
          push({
            title: "Digest complete",
            message: messageParts.join(" "),
          });
        } else {
          push({
            title: "Digest complete",
            message: "Finished ingesting venues.",
          });
        }
      } catch (error) {
        setIngestStatuses((prev) => ({
          ...prev,
          [artistId]: {
            taskId: prev[artistId]?.taskId,
            status: "failed",
            insertedEvents: null,
            venuesTouched: null,
            errors: [error instanceof Error ? error.message : "Unable to ingest venues."],
            updatedAt: Date.now(),
          },
        }));
        push({
          title: "Ingestion failed",
          message: error instanceof Error ? error.message : "Unable to ingest venues.",
        });
      }
    },
    [push],
  );

  const handleDeleteArtist = useCallback(
    async (artistId: number) => {
      setDeletingArtistId(artistId);
      try {
        await deleteResolvedArtist(artistId);
        push({ title: "Artist removed", message: "Resolved artist denied and removed." });
        await loadResolvedArtists();
      } catch (error) {
        push({
          title: "Deny failed",
          message: error instanceof Error ? error.message : "Unable to remove artist.",
        });
      } finally {
        setDeletingArtistId(null);
      }
    },
    [loadResolvedArtists, push],
  );

  const handleBulkAction = useCallback(
    async (action: "ingest" | "import" | "delete") => {
      if (selectedArtistIds.size === 0) {
        setMessage("Select at least one artist to run this action.");
        return;
      }
      const targets =
        action === "ingest"
          ? selectedArtists.filter((artist) => artist.bandsintown_slug)
          : selectedArtists;
      if (targets.length === 0) {
        setMessage(
          action === "ingest"
            ? "Selected artists need Bandsintown slugs before digestion."
            : "Select at least one artist to run this action.",
        );
        return;
      }

      const ids = targets.map((artist) => artist.id);
      setBulkAction(action);
      let successes = 0;
      for (const artistId of ids) {
        try {
          if (action === "ingest") {
            const response = await ingestResolvedArtist(artistId);
            setIngestStatuses((prev) => ({
              ...prev,
              [artistId]: {
                taskId: response.task_id,
                status: "queued",
                insertedEvents: null,
                venuesTouched: null,
                errors: [],
                updatedAt: Date.now(),
              },
            }));
          } else if (action === "import") {
            await importResolvedArtist(artistId);
          } else {
            await deleteResolvedArtist(artistId);
          }
          successes += 1;
        } catch (error) {
          push({
            title: `${action === "ingest" ? "Digest" : action === "import" ? "CRM import" : "Deny"} error`,
            message: error instanceof Error ? error.message : "Action failed.",
          });
        }
      }
      setBulkAction(null);
      setSelectedArtistIds(new Set());
      await loadResolvedArtists();
      push({
        title: "Batch complete",
        message: `${action === "ingest" ? "Digested" : action === "import" ? "Imported" : "Denied"} ${successes} artist${successes === 1 ? "" : "s"}.`,
      });
    },
    [selectedArtistIds, selectedArtists, loadResolvedArtists, push],
  );

  const formatIngestMessage = useCallback((status?: ArtistIngestStatus | null) => {
    if (!status) return null;
    if (status.status === "queued") {
      return "Queued…";
    }
    if (status.status === "in_progress") {
      return "Digesting venues…";
    }
    if (status.status === "complete") {
      return `Processed ${status.venuesTouched ?? 0} venues and ${status.insertedEvents ?? 0} events.`;
    }
    if (status.status === "complete_with_errors") {
      return `Completed with issues (${status.venuesTouched ?? 0} venues).`;
    }
    if (status.status === "failed") {
      const message = status.errors?.[0] || "Ingestion failed.";
      return message.length > 120 ? `${message.slice(0, 117)}…` : message;
    }
    return null;
  }, []);

  const hasPendingArtists = useMemo(
    () => resolvedArtists.some((artist) => !artist.crm_contact_id),
    [resolvedArtists],
  );

  return (
    <div className="aa-page-shell space-y-4">
      <section className="aa-glass-card p-4 space-y-4">
        <Tabs defaultValue="playlist" className="space-y-4">
          <TabsList className="w-full justify-start gap-2">
            <TabsTrigger value="playlist">Find Artist by Playlist</TabsTrigger>
            <TabsTrigger value="genre">Find Artists by Genre</TabsTrigger>
          </TabsList>
          <TabsContent value="playlist" className="space-y-3">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Find Artists from Playlist</h2>
              <p className="text-xs text-muted-foreground">Seed new artists directly from Spotify playlists.</p>
            </div>
            <form
              className="grid gap-4 md:grid-cols-[2fr,auto]"
              onSubmit={handlePlaylistSubmit}
            >
              <label className="flex flex-col text-sm text-foreground/70">
                Playlist URL or ID
                <input
                  type="text"
                  value={playlistInput}
                  onChange={(event) => setPlaylistInput(event.target.value)}
                  placeholder="https://open.spotify.com/playlist/…"
                  className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-primary/60 focus:outline-none"
                />
              </label>
              <div className="flex items-end justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-11 rounded-full uppercase tracking-widest text-xs"
                  onClick={handlePlaylistPreview}
                  disabled={previewLoading}
                >
                  {previewLoading ? "Previewing…" : "Preview"}
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  className="h-11 rounded-full uppercase tracking-widest text-xs"
                  disabled={isPlaylistSubmitting}
                >
                  {isPlaylistSubmitting ? "Finding…" : "Find from Playlist"}
                </Button>
              </div>
            </form>
            {previewError && <p className="text-sm text-rose-400">{previewError}</p>}
            {playlistPreview.length > 0 && (
              <>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    Showing {previewOffset + 1}–{previewOffset + playlistPreview.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full text-xs"
                      onClick={() => handlePreviewPageChange("previous")}
                      disabled={previewOffset === 0 || previewLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full text-xs"
                      onClick={() => handlePreviewPageChange("next")}
                      disabled={!previewHasMore || previewLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto rounded-2xl border border-border">
                  <table className="w-full min-w-[480px] text-sm text-foreground/80">
                    <thead className="bg-background/60 text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-4 py-2 text-left">#</th>
                        <th className="px-4 py-2 text-left">Artist</th>
                      </tr>
                    </thead>
                    <tbody>
                      {playlistPreview.map((artist, index) => (
                        <tr key={artist.spotify_id} className="border-t border-border">
                          <td className="px-4 py-2 text-muted-foreground">{index + 1}</td>
                          <td className="px-4 py-2">
                            <p className="font-semibold text-foreground">{artist.name}</p>
                            <p className="text-xs text-muted-foreground">{artist.genres?.join(", ") || "n/a"}</p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TabsContent>
          <TabsContent value="genre" className="space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Find Artists by Genre</h2>
              <p className="text-xs text-muted-foreground">
                Enter the EveryNoise genre slug to seed similar artists. Example:{" "}
                <span className="font-medium text-foreground/80">jamgrass</span>.
              </p>
            </div>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <label className="block text-sm text-foreground/70">
                Genre
                <input
                  type="text"
                  value={genreInput}
                  onChange={(event) => setGenreInput(event.target.value)}
                  placeholder="jamgrass"
                  className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-primary/60 focus:outline-none"
                />
              </label>
              <div className="flex justify-end">
                <Button
                  type="submit"
                  size="sm"
                  className="rounded-full uppercase tracking-widest text-xs"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Finding…" : "Start Find"}
                </Button>
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </section>

      {(taskId != null || bootstrapStatus !== "idle") && (
        <div className="rounded-xl border border-border/60 bg-background/30 px-4 py-3 space-y-1.5">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Task{" "}
              {taskId ? <span className="font-semibold text-foreground">{taskId.slice(0, 8)}</span> : "—"}
            </span>
            <span className={connected ? "text-emerald-400" : "text-muted-foreground"}>
              SSE {connected ? "connected" : "disconnected"}
            </span>
          </div>
          <div className="overflow-hidden rounded-full bg-muted h-1.5">
            <div
              className="h-1.5 rounded-full bg-primary transition-all"
              style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {progress.toFixed(1)}% · {bootstrapStatus}
          </p>
          {message && <p className="text-xs text-foreground/70">{message}</p>}
        </div>
      )}

      <section className="aa-glass-card p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              Resolved Artists{" "}
              <span className="font-normal text-muted-foreground">· {resolvedTotal.toLocaleString()}</span>
            </h2>
            <p className="text-xs text-muted-foreground">Add to CRM pushes the artist profile into contacts.</p>
            {resolvedPlaylistFilter && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-1 text-primary">
                  Filtered to playlist {resolvedPlaylistFilter}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full text-xs"
                  onClick={() => {
                    setResolvedPlaylistFilter(null);
                    setResolvedOffset(0);
                  }}
                >
                  Clear filter
                </Button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs ${hasPendingArtists ? "text-primary" : "text-emerald-300"}`}>
              {hasPendingArtists ? "Pending imports" : "All artists synced"}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-full text-xs"
              onClick={() => {
                void loadResolvedArtists();
              }}
              disabled={resolvedLoading}
            >
              {resolvedLoading ? "Refreshing…" : "Refresh"}
            </Button>
            <Button
              type="button"
              size="sm"
              className="rounded-full text-xs"
              onClick={() => handleBulkAction("import")}
              disabled={bulkAction === "import" || selectedCount === 0}
            >
              {bulkAction === "import" ? "Adding…" : `Add Selected (${selectedCount})`}
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="rounded-full text-xs"
              onClick={() => handleBulkAction("delete")}
              disabled={bulkAction === "delete" || selectedCount === 0}
            >
              {bulkAction === "delete" ? "Denying…" : `Deny Selected (${selectedCount})`}
            </Button>
          </div>
        </div>
        {renderResolvedPagination("top")}
        {resolvedError && <p className="text-sm text-rose-400">{resolvedError}</p>}
        <div className="space-y-2 md:hidden">
          {resolvedArtists.length === 0 && !resolvedLoading ? (
            <div className="rounded-xl border border-border bg-background/40 p-3 text-sm text-muted-foreground">
              No resolved artists yet. Run a find to populate this list.
            </div>
          ) : (
            resolvedArtists.map((artist) => {
              const spotifyUrl = artist.spotify_id ? `https://open.spotify.com/artist/${artist.spotify_id}` : null;
              const bandsintownUrl = artist.bandsintown_slug
                ? `https://www.bandsintown.com/a/${artist.bandsintown_slug}`
                : null;
              const imported = Boolean(artist.crm_contact_id);
              const ingestStatus = ingestStatuses[artist.id];
              const ingestStatusMessage = formatIngestMessage(ingestStatus);
              return (
                <div key={artist.id} className="rounded-xl border border-border bg-background/40 p-3 space-y-2">
                  <div className="flex items-start gap-3">
                    <label className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center md:min-h-[32px] md:min-w-[32px]">
                      <input
                        type="checkbox"
                        checked={selectedArtistIds.has(artist.id)}
                        onChange={() => toggleArtistSelection(artist.id)}
                        aria-label={`Select ${artist.name}`}
                        className="h-5 w-5 rounded border border-border bg-background/70 md:h-4 md:w-4"
                      />
                      <span className="sr-only">Select artist</span>
                    </label>
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{artist.name}</p>
                      <p className="text-xs text-muted-foreground">{artist.genre ?? "—"}</p>
                      <p className="text-xs text-muted-foreground">
                        Tracker: {artist.tracker_count != null ? artist.tracker_count.toLocaleString() : "—"}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs text-primary/80">
                        {spotifyUrl && (
                          <a href={spotifyUrl} target="_blank" rel="noreferrer">
                            Spotify ↗
                          </a>
                        )}
                        {bandsintownUrl && (
                          <a href={bandsintownUrl} target="_blank" rel="noreferrer">
                            Bandsintown ↗
                          </a>
                        )}
                      </div>
                    </div>
                    <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${imported ? "border-emerald-500/40 text-emerald-200" : "border-primary/40 text-primary"}`}>
                      {imported ? "In CRM" : "Pending"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      className="rounded-full text-xs"
                      onClick={() => handleImportArtist(artist.id)}
                      disabled={imported || importingArtistId === artist.id}
                    >
                      {imported ? "Added" : importingArtistId === artist.id ? "Adding…" : "Add to CRM"}
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="rounded-full text-xs"
                      onClick={() => handleDeleteArtist(artist.id)}
                      disabled={deletingArtistId === artist.id}
                    >
                      {deletingArtistId === artist.id ? "Denying…" : "Deny"}
                    </Button>
                  </div>
                  {ingestStatusMessage && (
                    <p className="text-xs text-muted-foreground">{ingestStatusMessage}</p>
                  )}
                </div>
              );
            })
          )}
        </div>
        <div className="hidden md:block overflow-x-auto rounded-2xl border border-border">
          <table className="w-full min-w-[640px] text-sm text-foreground/70">
            <thead className="bg-background/60 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left w-10">
                  <label className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center md:min-h-[32px] md:min-w-[32px]">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      aria-label="Select all artists"
                      className="h-5 w-5 rounded border border-border bg-background/70 md:h-4 md:w-4"
                    />
                    <span className="sr-only">Select all artists</span>
                  </label>
                </th>
                <th className="px-4 py-2 text-left">Artist</th>
                <th className="px-4 py-2 text-left">Genre</th>
                <th className="px-4 py-2 text-left">Tracker</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {resolvedArtists.length === 0 && !resolvedLoading ? (
                <tr>
                  <td className="px-4 py-6 text-center text-sm text-muted-foreground" colSpan={6}>
                    No resolved artists yet. Run a find to populate this list.
                  </td>
                </tr>
              ) : (
                resolvedArtists.map((artist) => {
                  const spotifyUrl = artist.spotify_id ? `https://open.spotify.com/artist/${artist.spotify_id}` : null;
                  const bandsintownUrl = artist.bandsintown_slug
                    ? `https://www.bandsintown.com/a/${artist.bandsintown_slug}`
                    : null;
                  const imported = Boolean(artist.crm_contact_id);
                  const ingestStatus = ingestStatuses[artist.id];
                  const ingestStatusMessage = formatIngestMessage(ingestStatus);
                  return (
                    <tr key={artist.id} className="border-t border-border">
                      <td className="px-4 py-3">
                        <label className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center md:min-h-[32px] md:min-w-[32px]">
                          <input
                            type="checkbox"
                            checked={selectedArtistIds.has(artist.id)}
                            onChange={() => toggleArtistSelection(artist.id)}
                            aria-label={`Select ${artist.name}`}
                            className="h-5 w-5 rounded border border-border bg-background/70 md:h-4 md:w-4"
                          />
                          <span className="sr-only">Select artist</span>
                        </label>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <p className="font-medium text-foreground">{artist.name}</p>
                          <div className="flex flex-wrap gap-3 text-xs text-primary/80">
                            {spotifyUrl && (
                              <a href={spotifyUrl} target="_blank" rel="noreferrer">
                                Spotify ↗
                              </a>
                            )}
                            {bandsintownUrl && (
                              <a href={bandsintownUrl} target="_blank" rel="noreferrer">
                                Bandsintown ↗
                              </a>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{artist.genre ?? "—"}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {artist.tracker_count != null ? artist.tracker_count.toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3">
                        {imported ? (
                          <span className="rounded-full border border-emerald-500/40 px-2 py-0.5 text-xs font-semibold text-emerald-200">
                            In CRM
                          </span>
                        ) : (
                          <span className="rounded-full border border-primary/40 px-2 py-0.5 text-xs font-semibold text-primary">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex flex-col items-end gap-1">
                          <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            size="sm"
                            className="rounded-full text-xs"
                            onClick={() => handleImportArtist(artist.id)}
                            disabled={imported || importingArtistId === artist.id}
                          >
                            {imported ? "Added" : importingArtistId === artist.id ? "Adding…" : "Add to CRM"}
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="rounded-full text-xs"
                            onClick={() => handleDeleteArtist(artist.id)}
                            disabled={deletingArtistId === artist.id}
                          >
                            {deletingArtistId === artist.id ? "Denying…" : "Deny"}
                          </Button>
                          </div>
                          {ingestStatusMessage && (
                            <p className="text-xs text-muted-foreground">{ingestStatusMessage}</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {renderResolvedPagination("bottom")}
      </section>

    </div>
  );
}
